import { useEffect } from 'react';
import { type Channel } from 'pusher-js';
import { getPusher, ensurePusher } from '../lib/pusher';

/**
 * Subscribe to a Pusher channel event.
 * Automatically unbinds on unmount or dependency change.
 *
 * @param channelName - e.g. `user-${uid}` — pass null/undefined to skip
 * @param eventName - e.g. 'item.updated'
 * @param handler - called with event data payload
 */
export function usePusherEvent<T = unknown>(
  channelName: string | null | undefined,
  eventName: string,
  handler: (data: T) => void,
): void {
  useEffect(() => {
    if (!channelName) return;

    let channel: Channel | undefined;
    let cancelled = false;

    ensurePusher().then(() => {
      if (cancelled) return;
      const pusher = getPusher();
      channel = pusher.subscribe(channelName);
      channel.bind(eventName, handler);
    });

    return () => {
      cancelled = true;
      if (channel) {
        channel.unbind(eventName, handler);
      }
    };
  }, [channelName, eventName, handler]);
}
